<template>
	<div class="component-container component-edit-account">
		<modal v-if="active" @close="closeModal">
			<template #header>
				<p class="modal-title">
					Edit Your Account
				</p>
			</template>

			<template #content>
				<div class="form-container">
					<div v-if="!$store.state.account.isManualLoginDenied" class="form-field">
						<label class="form-label">Current Password *</label>
						<input v-model="user.current_password" type="password" class="form-input" placeholder="Enter your existing password..." />
					</div>

					<br />

					<div class="form-field">
						<label class="form-label">Name *</label>
						<input v-model="user.name" type="text" class="form-input" placeholder="Enter your name..." />
					</div>
					<div class="form-field">
						<label class="form-label">Email Address *</label>
						<input v-model="user.email" type="email" class="form-input" placeholder="Enter your email address..." />
					</div>
					<div class="form-field">
						<label class="form-label">Mobile Number <span v-if="!$store.state.account.isManualLoginDenied && user.preferred2faMethod === 'sms'">*</span></label>
						<input v-model="user.mobileNumber" type="tel" class="form-input" placeholder="Enter your mobile number..." />
					</div>
					<div v-if="!$store.state.account.isManualLoginDenied" class="form-field">
						<label class="form-label">2FA Method *</label>
						<select v-model="user.preferred2faMethod" class="form-input">
							<option value="sms">
								SMS
							</option>
							<option value="email">
								Email
							</option>
							<option value="totp">
								Authenticator app
							</option>
						</select>
					</div>
					<div v-if="user.preferred2faMethod === 'totp' && !$store.state.account.isManualLoginDenied" class="form-field">
						<label class="form-label">
							<help label="user.authenticatior_app">Authenticator App</help>
						</label>
						<template v-if="user.totpConfigured">
							<div class="twofa-section">
								<p><fa class="success" :icon="['fas', 'check']" /> Setup OK</p>

								<button class="btn btn-create" @click="openModal()">
									Reset 2FA Setup
								</button>
							</div>
						</template>
						<template v-else>
							<div class="twofa-section">
								<p><fa class="warning fa-spin" :icon="['fas', 'spinner']" /> Awaiting setup</p>

								<button class="btn btn-create" @click="openModal()">
									Start 2FA Setup
								</button>
							</div>
						</template>
					</div>
					<div v-if="!$store.state.account.isManualLoginDenied" class="form-field">
						<label class="form-label">New Password</label>
						<input v-model="user.new_password" type="password" class="form-input" placeholder="Enter a new password..." />
					</div>
					<div v-if="!$store.state.account.isManualLoginDenied" class="form-field">
						<label class="form-label">Confirm New Password</label>
						<input v-model="user.new_password_confirm" type="password" class="form-input" placeholder="Confirm your new password..." />
					</div>
				</div>
			</template>

			<template #footer>
				<button class="btn btn-save" title="Update Account" @click="updateAccount">
					Update Account
				</button>
			</template>
		</modal>
		<modal v-if="show_2fa_modal" @close="close2faModal">
			<template #header>
				<p class="modal-title">
					Setup 2FA using an Authenticator App
				</p>
			</template>

			<template #content>
				<div class="two-columns">
					<div class="left-column">
						<p class="info">
							You now need to configure two factor authentication, using an authenticator app on your mobile device.
						</p>
						<div class="row">
							<div class="bullet-point">
								1
							</div>
							<div>
								<p class="title">
									Open your authenticator app
								</p>
								<p class="text">
									If you don't have one, they can be downloaded from your app store.
								</p>
							</div>
						</div>
						<div class="row">
							<div class="bullet-point">
								2
							</div>
							<div>
								<p class="title">
									Add a new account
								</p>
								<p class="text">
									Use the <b>+</b> or <b>Add Account</b> button in the app and select the QR code option to scan the code shown on the right.
								</p>
							</div>
						</div>
						<div class="row">
							<div class="bullet-point">
								3
							</div>
							<div>
								<p class="title">
									Enter the 6-digit code from the app below
								</p>
								<input
									ref="codeinput"
									v-model="verify_code"
									type="text"
									class="code"
									placeholder="Code..."
									@keydown.enter="verify2faCode"
								/>
							</div>
						</div>
					</div>
					<div class="qrcode">
						<img src="/api/authentication/2fa/qr-code.png" alt="QR Code">
					</div>
				</div>
			</template>

			<template #footer>
				<button class="btn btn-danger" title="Cancel" @click="close2faModal">
					Cancel
				</button>
				<button class="btn btn-success" title="Verify Code" @click="verify2faCode">
					Verify
				</button>
			</template>
		</modal>
	</div>
</template>

<script lang="ts">
	import { Options, Vue } from 'vue-class-component';
	import { GeneralHelper } from '../helper/general';
	import { HttpHelper } from '../helper/http';
	import { Messages } from '../enums/messages';
	import Modal from './Modal.vue';

	@Options({
		name: 'Edit Account',
		components: { Modal },
		props: ['active'],
		emits: ['change'],
		inject: ['http', 'helper'],
	})
	export default class EditAccount extends Vue {
		public http!: HttpHelper;
		public helper!: GeneralHelper;
		public active!: boolean;
		public show_2fa_modal = false;
		public verify_code = '';
		public user: any = {
			name: '',
			email: '',
			mobileNumber: '',
			preferred2faMethod: '',
			current_password: '',
			new_password: '',
			new_password_confirm: '',
			isTotpAuthenticationEnabled: false,
			totpConfigured: false,
		};

		public get userId(): any {
			return this.$store.state.account.id;
		}

		public closeModal(): void {
			this.$emit('change', false);
			this.populateUserObject();
		}

		public openModal(): void {
			this.show_2fa_modal = true;
		}

		public close2faModal(): void {
			this.show_2fa_modal = false;
			this.verify_code = '';
		}

		public async verify2faCode(): Promise<void> {
			if (this.verify_code.length <= 0) {
				this.$store.dispatch('notification/add', {
					type: 'warning',
					message: 'Code value can\'t be empty',
				});
				return;
			}

			const response = await this.http.post('/api/authentication/verify_2fa_code', { code: this.verify_code });

			if (response.status === 204) {
				this.$store.dispatch('notification/add', {
					type: 'success',
					message: 'Authenticator App successfully set up',
				});

				this.show_2fa_modal = false;
				this.verify_code = '';
				this.requestAccess();
			}
		}

		public async updateAccount(): Promise<void> {
			if (this.user.new_password !== '') {
				const errorMessage: boolean | string = this.helper.validatePassword(this.user.new_password, this.user.new_password_confirm);
				if (errorMessage !== false) {
					this.$store.dispatch('notification/add', {
						type: 'warning',
						message: String(errorMessage),
					});
					return;
				}
			}

			// Validate existing password.
			if (this.user.current_password === '' && !this.$store.state.account.isManualLoginDenied) {
				this.$store.dispatch('notification/add', {
					type: 'warning',
					message: 'You must enter your existing password before you can save changes.',
				});
				return;
			}

			// Define errors.
			const errorMessages: Array<string> = [];

			// Validate information.
			if (!this.helper.validateField('email', this.user.email)) {
				errorMessages.push('Email is not valid');
			}

			const mobile = String(this.user.mobileNumber ?? '').trim();

			if (mobile.length > 0 && !this.helper.validateField('phone', mobile)) {
				errorMessages.push('Mobile number is not valid');
			}

			if (this.user.preferred2faMethod === 'sms' && mobile.length === 0) {
				errorMessages.push('Mobile number is required for SMS 2FA');
			}

			if (this.user.name.length < 6 || this.user.name.split(' ').length < 2) {
				errorMessages.push('Full name field is too short or missing a last name');
			}

			if (this.user.preferred2faMethod === 'totp' && !this.user.totpConfigured) {
				errorMessages.push('2FA using Authentication App must be setup');
			}

			// Check for errors.
			if (errorMessages.length > 0) {
				this.$store.dispatch('notification/add', {
					type: 'warning',
					message: `There are problems with your form: ${errorMessages.join(', ')}.`,
				});
				return;
			}

			const response = await this.http.patch(`/api/users/${this.userId}`, this.user);
			if (response.status === 204) {
				this.$store.dispatch('notification/add', {
					type: 'success',
					message: Messages.ACCOUNT_CHANGE_SUCCESS,
				});
				this.$emit('change', false);
				this.user.current_password = '';
				this.user.new_password = '';
				this.user.new_password_confirm = '';

				this.requestAccess();
			}
		}

		public async requestAccess(): Promise<void> {
			if (!this.$store.state.account.isLoggedIn) return;
			const accessResponse = await this.http.post('/api/access', [], { autoRefresh: 'auto' });
			if (accessResponse.status !== 200) {
				return;
			}
			this.$store.dispatch('account/set', await accessResponse.json());
			this.populateUserObject();
		}

		public populateUserObject(): void {
			const account = this.$store.state.account;
			this.user.name = account.name;
			this.user.email = account.email;
			this.user.mobileNumber = account.mobileNumber;
			this.user.preferred2faMethod = account.preferred2faMethod;
			this.user.isTotpAuthenticationEnabled = account.isTotpAuthenticationEnabled;
			this.user.totpConfigured = account.totpConfigured;
		}

		public mounted(): void {
			this.populateUserObject();
		}
	}
</script>

<style lang="scss" scoped>
	@import '../assets/styles/base';

	.two-columns {
		display: grid;
		grid-template-columns: 1fr 0.5fr;

		.left-column {
			padding-right: 10px;
		}

		.info {
				margin-bottom: 20px;
				line-height: 1.3rem;
			}

		.row {
			margin-bottom: 20px;
			display: flex;

			.title {
				font-weight: 600;
				margin-bottom: 5px;
			}
			.text {
				line-height: 1.3rem;
			}
			.code {
				margin-top: 10px;
			}
		}
	}

	.bullet-point {
		font-family: "Inter", sans-serif;
		background-color: $create-color;
		color: $color_netcall-lightgrey;
		border-radius: 50%;
		-moz-border-radius: 50%;
        -webkit-border-radius: 50%;
		display: inline-block;
		font-weight: bold;
		line-height: 40px;
		font-size: 18px;
        margin-right: 5px;
        text-align: center;
        width: 40px;
		height: 40px;
		min-width: 40px;
		min-height: 40px;
		margin-right: 20px;
	}

	.twofa-section {
		display: flex;
		flex-direction: row;
		justify-content: space-between;
		align-items: center;
	}

	.success {
		color: $alert-success-colour;
	}

	.warning {
		color: $alert-warning-colour;
	}

	.notice {
		color: $link-colour;
	}
</style>
